// A. 同伴在場 — the companion lives on the practice screen, not just in the
// header. Fixed bottom-left, pointer-events-none, three moods:
//   idle    — slow gentle bob (breathing presence)
//   cheer   — jump + bounce on a correct answer
//   comfort — soft lean-in when the kid misses
// Speech bubbles (greeting / cheer / passive attribution) anchor above it.

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { companionById, type CompanionId } from '@skena/schema'

export type StageMood = 'idle' | 'cheer' | 'comfort'

export function CompanionStage({
  companionId,
  mood,
  bubble,
}: {
  companionId: CompanionId
  mood: StageMood
  bubble: string | null
}) {
  const reducedMotion = useReducedMotion()
  const companion = companionById(companionId)

  const moodAnimate = reducedMotion
    ? {}
    : mood === 'cheer'
      ? { y: [0, -18, 0, -8, 0], scale: [1, 1.08, 1, 1.04, 1], rotate: 0 }
      : mood === 'comfort'
        ? { y: 2, rotate: -6, scale: 0.97 }
        : { y: [0, -3, 0], rotate: 0, scale: 1 }
  const moodTransition =
    mood === 'idle'
      ? { duration: 2.4, repeat: Infinity, ease: 'easeInOut' as const }
      : { duration: mood === 'cheer' ? 0.7 : 0.4, ease: 'easeOut' as const }

  return (
    <div className="pointer-events-none fixed bottom-4 left-3 z-20 flex flex-col items-start gap-1.5">
      <AnimatePresence mode="wait">
        {bubble ? (
          <motion.div
            key={bubble}
            role="status"
            initial={{ opacity: 0, y: 6, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            transition={{ type: 'spring', stiffness: 400, damping: 24 }}
            className="max-w-[240px] rounded-2xl rounded-bl-sm bg-white/95 px-3 py-1.5 text-sm font-semibold text-[var(--color-fg)] shadow-md"
          >
            {bubble}
          </motion.div>
        ) : null}
      </AnimatePresence>
      <motion.img
        src={companion.cardUrl}
        alt=""
        aria-hidden="true"
        className="h-16 w-16 rounded-2xl object-cover shadow-md ring-2 ring-white/80"
        animate={moodAnimate}
        transition={moodTransition}
      />
    </div>
  )
}
